import React from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import NotFound from "./NotFound";
import { useSEO } from "../lib/useSEO";
import { ACCIDENTS } from "../data/accidents";
import { OHIO_CITIES, SERVICES } from "../data/content";
import { ArrowRight, Scale, ShieldCheck, FileText, MapPin, HelpCircle, CheckCircle2, Calculator } from "lucide-react";

const SITE_URL = "https://instabridgefunding.com";

export default function AccidentDetail() {
  const { slug } = useParams();
  const accident = ACCIDENTS.find((a) => a.slug === slug);

  useSEO(
    accident ? accident.meta_title : "Accident type not found",
    accident ? accident.meta_description : "",
    accident
      ? {
          canonical: `${SITE_URL}/accidents/${accident.slug}`,
          suffix: false,
          jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": accident.schema_type || "Service",
                name: `${accident.label} lawsuit funding`,
                serviceType: "Pre-settlement legal funding",
                provider: { "@type": "Organization", name: "Instabridge Funding" },
                areaServed: { "@type": "State", name: "Ohio" },
                description: accident.intro,
              },
              {
                "@type": "FAQPage",
                mainEntity: (accident.faqs || []).map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
                  { "@type": "ListItem", position: 2, name: "Cases we fund", item: `${SITE_URL}/how-it-works` },
                  { "@type": "ListItem", position: 3, name: accident.label, item: `${SITE_URL}/accidents/${accident.slug}` },
                ],
              },
            ],
          },
        }
      : { suffix: false, noindex: true },
  );

  if (!accident) return <NotFound />;

  const relatedCities = (accident.related_city_slugs || [])
    .map((s) => OHIO_CITIES.find((c) => c.slug === s))
    .filter(Boolean);
  const relatedServices = (accident.related_services || [])
    .map((s) => SERVICES.find((sv) => sv.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white" data-testid={`accident-page-${accident.slug}`}>
      <Navbar />

      <div className="border-b border-[#E5E7EB] py-3">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 text-xs text-[#6B7280]">
          <Link to="/" className="hover:text-[#0A2540]">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/how-it-works" className="hover:text-[#0A2540]">Cases we fund</Link>
          <span className="mx-2">/</span>
          <span className="text-[#0A2540]">{accident.label}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#F9FAFB] py-16 lg:py-20 border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="uppercase-label mb-5 flex items-center gap-2"><Scale className="w-3.5 h-3.5" /> {accident.plural}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-light leading-[1.05]">
            <span className="font-semibold">{accident.hero_title.replace(".", "")}</span>
          </h1>
          <p className="mt-5 text-lg text-[#374151] leading-relaxed max-w-3xl font-medium">{accident.hero_subtitle}</p>
          <p className="mt-4 text-base text-[#6B7280] leading-relaxed max-w-3xl">{accident.intro}</p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/apply">
              <Button className="btn-primary-navy rounded-full h-12 px-6" data-testid={`accident-${accident.slug}-apply-btn`}>
                Apply for {accident.label.toLowerCase()} funding <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/tools/funding-cost-calculator">
              <Button variant="outline" className="rounded-full h-12 px-6" data-testid={`accident-${accident.slug}-calc-btn`}>
                <Calculator className="w-4 h-4 mr-2" /> Estimate funding cost
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-12">
            {/* What it is */}
            <div>
              <div className="uppercase-label mb-3">The case type</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">What a {accident.label.toLowerCase()} case is.</h2>
              <p className="mt-4 text-[#374151] leading-relaxed">{accident.what_it_is}</p>
            </div>

            {/* Typical defendants */}
            <div>
              <div className="uppercase-label mb-3">Who we typically sue</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">Common defendants in these cases.</h2>
              <ul className="mt-5 space-y-2">
                {accident.typical_defendants.map((d) => (
                  <li key={d} className="flex items-start gap-3 p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-[#0066FF] mt-0.5 flex-shrink-0" />
                    <span className="text-[#374151] text-sm">{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Evidence */}
            <div>
              <div className="uppercase-label mb-3">Evidence that matters</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">What strengthens the case (and your funding chances).</h2>
              <ul className="mt-5 space-y-2">
                {accident.evidence_that_strengthens.map((e) => (
                  <li key={e} className="flex items-start gap-3 p-3 bg-white border border-[#E5E7EB] rounded-lg">
                    <FileText className="w-4 h-4 text-[#0066FF] mt-0.5 flex-shrink-0" />
                    <span className="text-[#374151] text-sm">{e}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Common scenarios */}
            <div>
              <div className="uppercase-label mb-3">Case scenarios</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">How these cases actually look.</h2>
              <div className="mt-6 space-y-4">
                {accident.common_scenarios.map((s) => (
                  <div key={s.title} className="p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                    <div className="font-display font-semibold text-[#0A2540]">{s.title}</div>
                    <p className="mt-2 text-sm text-[#374151] leading-relaxed">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Statute + settlement range */}
            <div>
              <div className="uppercase-label mb-3">Legal context</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">Statute, damages, and Ohio-specific rules.</h2>
              <div className="mt-5 space-y-4">
                <div className="p-5 bg-white border border-[#E5E7EB] rounded-lg">
                  <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-1">Statute of limitations</div>
                  <p className="text-sm text-[#374151] leading-relaxed">{accident.statute_note}</p>
                </div>
                <div className="p-5 bg-white border border-[#E5E7EB] rounded-lg">
                  <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-1">Typical settlement range</div>
                  <p className="text-sm text-[#374151] leading-relaxed">{accident.settlement_range_note}</p>
                </div>
              </div>
            </div>

            {/* Funding workflow */}
            <div>
              <div className="uppercase-label mb-3">Our funding process</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">How Instabridge funds {accident.plural}.</h2>
              <p className="mt-4 text-[#374151] leading-relaxed">{accident.funding_workflow}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/how-it-works" className="text-sm text-[#0066FF] hover:underline font-medium">Full funding process →</Link>
                <Link to="/faq" className="text-sm text-[#0066FF] hover:underline font-medium">Read the FAQ →</Link>
              </div>
            </div>

            {/* FAQs */}
            <div>
              <div className="uppercase-label mb-3">Common questions</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">Questions plaintiffs actually ask.</h2>
              <div className="mt-6 space-y-4">
                {accident.faqs.map((f, i) => (
                  <div key={i} className="p-5 bg-white border border-[#E5E7EB] rounded-lg" data-testid={`accident-faq-${i}`}>
                    <div className="flex items-start gap-2">
                      <HelpCircle className="w-4 h-4 text-[#0066FF] mt-1 flex-shrink-0" />
                      <div>
                        <div className="font-display font-semibold text-[#0A2540]">{f.q}</div>
                        <p className="mt-2 text-sm text-[#374151] leading-relaxed">{f.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="bg-[#0A2540] text-white rounded-2xl p-7 sticky top-24">
              <div className="uppercase-label !text-white/60 mb-3">Fast facts</div>
              <h3 className="font-display text-xl font-semibold">{accident.label} funding</h3>
              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Advance range</dt><dd className="font-medium">$500–$25,000</dd></div>
                <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Recourse</dt><dd className="font-medium">Non-recourse</dd></div>
                <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Credit check</dt><dd className="font-medium">None</dd></div>
                <div className="flex justify-between"><dt className="text-white/60">Funded in</dt><dd className="font-medium">24 hours*</dd></div>
              </dl>
              <p className="mt-3 text-[10px] text-white/50">*After attorney verification.</p>
              <Link to="/apply" className="block mt-6">
                <Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-11 w-full" data-testid={`accident-${accident.slug}-sidebar-apply`}>
                  Apply now
                </Button>
              </Link>
              <Link to="/how-it-works" className="block text-center text-sm text-white/60 hover:text-white mt-4">← See all cases we fund</Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Related services + cities */}
      <section className="py-16 bg-[#F9FAFB] border-t border-[#E5E7EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-2xl text-[#0A2540] font-semibold">Related services</h2>
            <p className="text-sm text-[#6B7280] mt-2">Instabridge offerings that support {accident.plural}.</p>
            <ul className="mt-6 space-y-3">
              {relatedServices.map((sv) => (
                <li key={sv.slug}>
                  <Link to={`/services/${sv.slug}`} className="block bg-white border border-[#E5E7EB] hover:border-[#0066FF] rounded-lg p-4 transition-colors">
                    <div className="font-display font-semibold text-[#0A2540]">{sv.name}</div>
                    <div className="text-sm text-[#6B7280] mt-1">{sv.short}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl text-[#0A2540] font-semibold">
              <MapPin className="w-5 h-5 inline text-[#0066FF] mr-2 mb-1" />Ohio cities where we fund these cases
            </h2>
            <p className="text-sm text-[#6B7280] mt-2">{accident.plural} we've funded across Ohio.</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {relatedCities.map((c) => (
                <Link key={c.slug} to={`/states/ohio/${c.slug}`} className="bg-white border border-[#E5E7EB] hover:border-[#0066FF] rounded-lg px-4 py-3 transition-colors">
                  <div className="font-display font-semibold text-[#0A2540] text-sm">{c.name}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{c.courts}</div>
                </Link>
              ))}
            </div>
            <Link to="/states/ohio" className="mt-4 inline-block text-sm text-[#0066FF] hover:underline font-medium">See all Ohio pages →</Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0066FF]/10 border border-[#0066FF]/25 text-[#0052CC] text-xs font-semibold uppercase tracking-wider mb-5">
            <ShieldCheck className="w-3.5 h-3.5" /> If you lose, you owe $0
          </div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-semibold">Ready to apply?</h2>
          <p className="mt-3 text-[#6B7280]">5 minutes. No obligation. Funding decisions in hours.</p>
          <Link to="/apply" className="inline-block mt-8">
            <Button className="btn-primary-navy rounded-full h-12 px-8" data-testid={`accident-${accident.slug}-bottom-apply`}>
              Start your application <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
